'use client'

import { Check, ChevronsUpDown } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import useLayout from '@/hooks/useLayout'
import { cn } from '@/lib/utils'

type Props = {
  municipalities: {
    id: string
    name: string
  }[]
  onValueChange?(value: string): void
  value?: string
}

export const MunicipalityInput = ({ municipalities, onValueChange, value }: Props) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [layout, bindLayout] = useLayout()

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <Button
          aria-expanded={open}
          className="
            flex
            h-12
            w-full
            rounded-md
            border
            border-input
            bg-field
            px-3
            py-2
            text-base
            file:border-0
            file:bg-transparent
            file:text-sm
            file:font-medium
            file:text-foreground
            placeholder:text-muted-foreground
            focus-visible:outline-none
            data-[state=open]:ring-1
            data-[state=open]:ring-info
            data-[state=open]:border-info
            focus-visible:ring-1
            focus-visible:ring-info
            focus-visible:border-info
            disabled:cursor-not-allowed
            disabled:opacity-50
            md:text-sm
            justify-between
          "
          role="combobox"
          variant="outline"
          {...bindLayout}
        >
          {value
            ? municipalities.find(municipality => municipality.id === value)?.name
            : t('formInputs.municipality.placeholder')}
          <ChevronsUpDown className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-0" style={{ width: layout.width }}>
        <Command>
          <CommandInput
            className="h-11"
            placeholder={t('formInputs.municipality.searchPlaceholder')}
          />
          <CommandList>
            <CommandEmpty>{t('formInputs.municipality.noSearchResults')}</CommandEmpty>
            <CommandGroup>
              {municipalities.map(municipality => (
                <CommandItem
                  key={municipality.id}
                  onSelect={name => {
                    const value = municipalities.find(m => m.name === name)?.id

                    if (!value) {
                      console.error('No municipality found for name:', name)

                      return
                    }

                    onValueChange?.(value)
                    setOpen(false)
                  }}
                  value={municipality.name}
                >
                  {municipality.name}
                  <Check
                    className={cn(
                      'ml-auto',
                      value === municipality.id ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
